import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/Button";
import { formatDisplayDate } from "@/utils/formatDisplayDate";
import { AlertTriangle, Archive } from "lucide-react";
import { api } from "@/services/api";
import { computeEditAccessEndDate, checkEditAccessExpired } from "@/hooks/useEditAccess";

const UnauthorizedPage = () => {
  const navigate = useNavigate();

  const { data: session } = useQuery({
    queryKey: ["pab-session", "active"],
    queryFn: api.pabSessions.getActiveSession,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  const { data: weeklyPlanData } = useQuery({
    queryKey: ["pab-session", "weekly-plan", session?.id],
    queryFn: () => api.pabSessions.getWeeklyPlan(session!.id, 12),
    // A board with no scheduled sessions has no starts_on/ends_on, and
    // /weekly-plan answers 400 "Session active dates are not set." rather than
    // an empty plan -- so there is nothing to ask for until they exist.
    enabled: !!session?.id && !!session?.activeFrom && !!session?.activeTill,
    staleTime: 0,
  });

  const editAccessEndDate = useMemo(
    () => computeEditAccessEndDate(weeklyPlanData?.wrapupDate),
    [weeklyPlanData?.wrapupDate],
  );

  const isEditAccessExpired = useMemo(
    () => checkEditAccessExpired(editAccessEndDate),
    [editAccessEndDate],
  );

  const editAccessEndDateFormatted = useMemo(
    () => (editAccessEndDate ? formatDisplayDate(editAccessEndDate, { variant: "date", length: "long" }) : null),
    [editAccessEndDate],
  );

  if (isEditAccessExpired) {
    return (
      <div className="rounded-lg bg-theme-bg-surface p-16 shadow-xl">
        <div className="mb-12 flex justify-center">
          <Archive className="h-32 w-32 text-theme-text-muted" />
        </div>

        <div className="mb-12 text-center">
          <h1 className="text-2xl font-bold text-theme-text-heading">Personal Advisory Board Archived</h1>
          <p className="mt-4 text-theme-text-secondary">
            This Personal Advisory Board has been archived — edit access ended
            {editAccessEndDateFormatted ? ` on ${editAccessEndDateFormatted}` : ""}.
            You can still view all data in read-only mode.
          </p>
        </div>

        <div className="flex justify-center space-x-8">
          <Button variant="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>

          <Button variant="ghost" onClick={() => navigate("/home")}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-theme-bg-surface p-16 shadow-xl">
      <div className="mb-12 flex justify-center">
        <AlertTriangle className="h-32 w-32 text-warning-500" />
      </div>

      <div className="mb-12 text-center">
        <h1 className="text-2xl font-bold text-theme-text-heading">Access Denied</h1>
        <p className="mt-4 text-theme-text-secondary">You don't have permission to access this page.</p>
      </div>

      <div className="flex justify-center space-x-8">
        <Button variant="primary" onClick={() => navigate(-1)}>
          Go Back
        </Button>

        <Button variant="ghost" onClick={() => navigate("/home")}>
          Go to Home
        </Button>
      </div>
    </div>
  );
};

export default UnauthorizedPage;
